import { useApolloClient } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useMemo, useState } from 'react';

import {
  MeasureSchemaType,
  MeasureValidationError,
  UseSubmitMeasureResponseCallbackResponse,
  useGetMeasureSchemaByType,
  useSubmitMeasureResponseCallback,
} from '@grow-therapy-team/seedling-components';
import type { MeasureBundleSourceType } from '@grow-therapy-team/seedling-components';

import { Server } from '../../apollo/types';
import { logger } from '../../datadog';
import { toast } from '../../toasts';
import {
  ProviderDrawerState,
  drawerStateAtom,
  providerAtom,
  selectedBundleAtom,
  selectedClientInfoAtom,
  selectedClientUserShortIdAtom,
} from '../state';
import {
  GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID,
  type GetPatientMeasuresByPatientShortIdQueryData,
  useApplyPatientMeasuresQueryResult,
} from '../useGetMeasuresByPatientShortId';
import { GET_PATIENT_BY_PATIENT_SHORT_ID_GQL } from '../useGetPatientByShortId';
import { findMeasureBundleInfoByResponseShortId, sortMeasureBundles } from './utils';

type SubmitMeasureResponseArgs = Parameters<ReturnType<typeof useSubmitMeasureResponseCallback>[0]>[0] & {
  cSsrsSourceType?: MeasureBundleSourceType;
};

type MeasureResponseSubmissionResultData = Awaited<ReturnType<UseSubmitMeasureResponseCallbackResponse[0]>>['data'];

function useHandleSuccessfulCSSRSMeasureSubmissionCallback() {
  const apolloClient = useApolloClient();
  const applyPatientMeasures = useApplyPatientMeasuresQueryResult();
  const getPatientShortId = useAtomCallback((get) => get(selectedClientInfoAtom)?.patientShortId);
  const setSelectedBundle = useSetAtom(selectedBundleAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);

  const updateSelectedMeasureBundle = useCallback(
    async (measureResponseData: MeasureResponseSubmissionResultData) => {
      const patientShortId = getPatientShortId();
      const submittedId = measureResponseData?.submitMeasureResponse?.measureResponse?.measureResponseShortId;

      if (!patientShortId) {
        setSelectedBundle(undefined);
        return;
      }

      // Lazy `useQuery` defaults to cache-first; after submit the cache can still hold pre-submit bundles until
      // we bypass it and apply results to Jotai explicitly (see useApplyPatientMeasuresQueryResult).
      const { data } = await apolloClient.query<GetPatientMeasuresByPatientShortIdQueryData>({
        query: GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID,
        variables: { patientShortId },
        fetchPolicy: 'network-only',
        context: { server: Server.MONOLITH },
      });

      if (data) {
        applyPatientMeasures(data);
      }

      const bundles = data?.patientChart?.measureBundles ?? [];
      const sortedBundles = sortMeasureBundles(bundles, 'createdAt');

      if (submittedId && sortedBundles.length > 0) {
        const { measureBundle } = findMeasureBundleInfoByResponseShortId(sortedBundles, submittedId);
        setSelectedBundle(measureBundle);
      } else {
        setSelectedBundle(undefined);
      }
    },
    [apolloClient, applyPatientMeasures, getPatientShortId, setSelectedBundle],
  );

  return useCallback(
    (measureResponseData: MeasureResponseSubmissionResultData) => {
      updateSelectedMeasureBundle(measureResponseData);
      setDrawerState(ProviderDrawerState.MEASURE_RESPONSES);
    },
    [updateSelectedMeasureBundle, setDrawerState],
  );
}

export const useSubmitCSSRSMeasureCallback = (
  measureSchemaType: MeasureSchemaType,
  previousResponseShortIdFromPrompt: string | null,
  cSsrsSourceType: MeasureBundleSourceType,
): [() => Promise<void>, { isLoading: boolean }] => {
  const startTimeMs = useMemo(() => Date.now(), []);

  const getProviderShortId = useAtomCallback((get) => get(providerAtom)?.userData?.shortId);
  const getClientUserShortId = useAtomCallback((get) => get(selectedClientUserShortIdAtom));
  const getPatientShortId = useAtomCallback((get) => get(selectedClientInfoAtom)?.patientShortId);
  const { data: cssrsSchemaData } = useGetMeasureSchemaByType(measureSchemaType);
  const [submitMeasure] = useSubmitMeasureResponseCallback();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const handleSuccessfulCSSRSMeasureSubmission = useHandleSuccessfulCSSRSMeasureSubmissionCallback();

  const handleSubmitMeasure = useCallback(async () => {
    const clientUserShortId = getClientUserShortId();
    const providerShortId = getProviderShortId();
    if (!clientUserShortId || !cssrsSchemaData?.measureSchema || !providerShortId) {
      logger.error('Could not submit measure due to missing values');
      toast.error('Unable to submit form due to missing values.');
      return;
    }

    try {
      setIsLoading(true);
      const patientShortId = getPatientShortId();
      const submitArgs: SubmitMeasureResponseArgs = {
        clientUserShortId: clientUserShortId,
        schema: cssrsSchemaData.measureSchema,
        providerShortId,
        shouldShowErrorToast: false,
        durationSeconds: Math.max(0, Math.round((Date.now() - startTimeMs) / 1000)),
        apolloOptions: {
          awaitRefetchQueries: true,
          refetchQueries: patientShortId
            ? [{ query: GET_PATIENT_BY_PATIENT_SHORT_ID_GQL, variables: { patientShortId } }]
            : [GET_PATIENT_BY_PATIENT_SHORT_ID_GQL],
        },
        previousResponseShortId: previousResponseShortIdFromPrompt ?? undefined,
        cSsrsSourceType,
      };
      const { data: measureResponseData } = await submitMeasure(submitArgs);

      handleSuccessfulCSSRSMeasureSubmission(measureResponseData);
    } catch (err) {
      if (!(err instanceof MeasureValidationError)) {
        logger.error('Could not submit CSSRS measure', { clientUserShortId }, err as Error);
        toast.error('Unable to submit form due to an unexpected error.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    getClientUserShortId,
    getPatientShortId,
    getProviderShortId,
    cssrsSchemaData,
    previousResponseShortIdFromPrompt,
    cSsrsSourceType,
    submitMeasure,
    handleSuccessfulCSSRSMeasureSubmission,
    startTimeMs,
  ]);

  return [handleSubmitMeasure, { isLoading }];
};
